import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Settings as SettingsIcon, LifeBuoy, DatabaseBackup, CheckCircle2 } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sar, setSar] = useState({ name: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  useEffect(() => {
    api.get("/settings/sar").then((r) => setSar(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSar((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/sar", sar); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const exportSnapshot = async () => {
    setExporting(true);
    try {
      const r = await api.post("/settings/export-snapshot");
      setLastExport(r.data);
      toast.success(`Snapshot saved — ${r.data.total_docs} records captured`);
    } catch { toast.error("Snapshot export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6 fade-up max-w-xl" data-testid="settings-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Organisation</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
          <SettingsIcon className="h-8 w-8 text-lime-500" /> Settings
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>
      ) : (
        <div className="bg-white border border-orange-200 rounded-xl p-6 space-y-4">
          <div>
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-orange-500" /> Default SAR / Emergency Contact</h2>
            <p className="text-sm text-slate-500 mt-1">Shown on every job unless overridden on the job itself.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Name</Label>
              <Input data-testid="sar-name-input" value={sar.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="e.g. AMSA / Chief Pilot" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
              <Input data-testid="sar-phone-input" value={sar.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="e.g. 1800 815 257" /></div>
          </div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea data-testid="sar-notes-input" value={sar.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" rows={2} placeholder="Flight following, procedures, area contacts" /></div>
          <Button data-testid="save-sar-btn" onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">{saving ? "Saving…" : "Save Settings"}</Button>
        </div>
      )}

      {isAdmin && !loading && (
        <div className="bg-white border border-sky-200 rounded-xl p-6 space-y-4" data-testid="deployment-data-card">
          <div>
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><DatabaseBackup className="h-5 w-5 text-sky-500" /> Deployment data snapshot</h2>
            <p className="text-sm text-slate-500 mt-1">Capture all current data (jobs, clients, assets, training courses, VFRG, certificates, etc.) into the deployment snapshot. Your <span className="font-medium">next deployment</span> will load this exact content into the live site.</p>
          </div>
          <Button data-testid="export-snapshot-btn" onClick={exportSnapshot} disabled={exporting} className="bg-sky-500 hover:bg-sky-400 text-white font-semibold">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-1.5" />} {exporting ? "Capturing…" : "Export current data for deployment"}
          </Button>
          {lastExport && (
            <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 p-3 text-sm text-sky-900" data-testid="export-snapshot-result">
              <CheckCircle2 className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
              <span>Snapshot captured — <span className="font-semibold">{lastExport.total_docs}</span> records (version {lastExport.version}). Deploy now to push this content live.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
